import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { formatDate } from '../lib/dateUtils';
import { useSystemDate } from '../hooks/useSystemDate';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useTranslation } from '../hooks/useTranslation';

export const LeftPanel = () => {
  const { t } = useTranslation();
  const companies = useLiveQuery(async () => {
    const comps = await db.companies.toArray();
    
    const compsWithDates = await Promise.all(comps.map(async (comp) => {
      const activeVouchers = await db.vouchers
        .where('companyId').equals(comp.id!)
        .filter(v => !v.deleted)
        .toArray();
        
      let lastDateDisplay = '-';
      if (activeVouchers.length > 0) {
        const maxDateStr = activeVouchers.reduce((max, v) => v.date > max ? v.date : max, activeVouchers[0].date);
        const d = new Date(maxDateStr);
        if (!isNaN(d.getTime())) {
          const day = d.getDate().toString().padStart(2, '0');
          const month = (d.getMonth() + 1).toString().padStart(2, '0');
          const year = d.getFullYear();
          lastDateDisplay = `${day}-${month}-${year}`;
        }
      }
      return { ...comp, calculatedLastEntry: lastDateDisplay };
    }));
    return compsWithDates;
  }) || [];
  const activeCompany = useStore((state) => state.activeCompany);
  const setActiveCompany = useStore((state) => state.setActiveCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  const systemDate = useSystemDate();
  
  const stats = useDashboardStats();

  const handleCompanyClick = async (company: any) => {
    if (company.id === activeCompany?.id) return;
    
    // Deactivate other companies in DB
    await db.companies.where('isActive').equals(1).modify({ isActive: 0 });
    // Activate selected
    await db.companies.update(company.id, { isActive: 1 });
    setActiveCompany({ ...company, isActive: 1 });
  };
  
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val || 0);
  };
  

  return (
  <div className="hidden md:flex flex-1 flex-col bg-white border-l border-gray-200 overflow-hidden">
    <div className="flex justify-between border-b-[3px] border-[#1e8585] p-5 text-[#1e8585] font-bold text-sm shrink-0">
      <div>{t('gateway.currentPeriod')}<br /><span className="text-black font-normal text-base">{formatDate(fromDate)} to {formatDate(toDate)}</span></div>
      <div className="text-right">{t('gateway.currentDate')}<br /><span className="text-black font-normal text-base">{formatDate(systemDate)}</span></div>
    </div>
    <div className="flex bg-[#1e8585] text-white px-6 py-2 text-[11px] font-semibold shrink-0 gap-6">
      <span className="flex-1">{t('gateway.nameOfCompany')}</span>
      <span className="w-[70px] text-center">Type</span>
      <span className="w-[90px] text-right">Opening Bal.</span>
      <span className="w-[90px] text-right">Last Entry</span>
    </div>
    <div className="overflow-y-auto bg-[#fdfdfd] border-b border-gray-200 shrink-0 min-h-[140px] max-h-[200px] scrollbar-thin">
      {companies.length > 0 ? companies.map((company: any, idx: number) => {
        const isActive = company.id === activeCompany?.id;
        const nature = company.businessNature || 'general';
        const natureLabels: Record<string, string> = { general: 'General', pharmacy: 'Pharma', restaurant: 'F&B', jewellery: 'Jewel', realestate: 'Realty', education: 'Edu', transport: 'Fleet', hotel: 'Hotel' };
        return (
          <div
            key={idx}
            className={`flex items-center px-6 py-3 text-[12px] border-b border-gray-100 cursor-pointer transition-colors gap-6 ${isActive ? 'font-bold bg-[#e6f2f2] border-l-[3px] border-l-[#1e8585]' : 'text-gray-500 hover:bg-gray-50'}`}
            onClick={() => handleCompanyClick(company)}
          >
            <span className={`flex-1 truncate ${isActive ? 'text-black' : ''}`}>{company.name}</span>
            <span className="w-[70px] text-center">
              <span className={`text-[9px] font-bold px-1.5 py-[1px] rounded-full ${
                nature === 'pharmacy' ? 'bg-emerald-100 text-emerald-700'
                : nature === 'general' ? 'bg-gray-100 text-gray-500'
                : 'bg-blue-100 text-blue-700'
              }`}>{natureLabels[nature] || nature}</span>
            </span>
            <span className={`w-[90px] text-right text-[11px] ${isActive ? 'text-[#1e8585] font-bold' : ''}`}>
              {formatCurrency(0)}
            </span>
            <span className={`w-[90px] text-right text-[11px] ${isActive ? 'text-black' : ''}`}>{company.calculatedLastEntry || '-'}</span>
          </div>
        );
      }) : (
        <div className="px-4 py-2 text-[12px] text-gray-500">Loading companies...</div>
      )}
    </div>
    <div className="flex-1 overflow-y-auto bg-[#f1f5f9] flex flex-col mt-2 border-t border-gray-200">
      {stats && (
        <>
          {/* Business Insights — KPI Grid */}
          <div className="p-3 shrink-0">
            <div className="text-[#1e8585] font-bold text-[10px] uppercase tracking-wide mb-2 flex items-center">
              <svg className="w-3.5 h-3.5 mr-1.5 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg> {t('gateway.businessOverview')}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              <div className="bg-white p-2 border border-gray-200 rounded shadow-sm border-l-[3px] border-l-[#1e8585]">
                <div className="text-[9px] text-gray-500 font-semibold mb-0.5 uppercase tracking-tight">{ t('dash.totalSales') }</div>
                <div className="text-[13px] font-bold text-[#1e8585] leading-tight">{formatCurrency(stats.totalSales)}</div>
              </div>
              <div className="bg-white p-2 border border-gray-200 rounded shadow-sm border-l-[3px] border-l-orange-500">
                <div className="text-[9px] text-gray-500 font-semibold mb-0.5 uppercase tracking-tight">{t('dash.totalPurchase')}</div>
                <div className="text-[13px] font-bold text-orange-600 leading-tight">{formatCurrency(stats.totalPurchase)}</div>
              </div>
              <div className="bg-white p-2 border border-gray-200 rounded shadow-sm border-l-[3px] border-l-emerald-500">
                <div className="text-[9px] text-gray-500 font-semibold mb-0.5 uppercase tracking-tight">{t('dash.cashBalance')}</div>
                <div className="text-[13px] font-bold text-emerald-600 leading-tight">{formatCurrency(stats.cashBalance)}</div>
              </div>
              <div className="bg-white p-2 border border-gray-200 rounded shadow-sm border-l-[3px] border-l-blue-500">
                <div className="text-[9px] text-gray-500 font-semibold mb-0.5 uppercase tracking-tight">{t('dash.bankBalance')}</div>
                <div className="text-[13px] font-bold text-blue-600 leading-tight">{formatCurrency(stats.bankBalance)}</div>
              </div>
              <div className="bg-white p-2 border border-gray-200 rounded shadow-sm border-l-[3px] border-l-cyan-500">
                <div className="text-[9px] text-gray-500 font-semibold mb-0.5 uppercase tracking-tight">{t('dash.receivables')}</div>
                <div className="text-[13px] font-bold text-cyan-600 leading-tight">{formatCurrency(stats.receivables)}</div>
              </div>
              <div className="bg-white p-2 border border-gray-200 rounded shadow-sm border-l-[3px] border-l-rose-400">
                <div className="text-[9px] text-gray-500 font-semibold mb-0.5 uppercase tracking-tight">{t('dash.payables')}</div>
                <div className="text-[13px] font-bold text-rose-600 leading-tight">{formatCurrency(stats.payables)}</div>
              </div>
            </div>
          </div>

          {/* {t('dash.booksHealth')} */}
          <div className="px-3 pb-3">
            <div className="text-[#1e8585] font-bold text-[10px] uppercase tracking-wide mb-2 flex items-center">
              <svg className="w-3.5 h-3.5 mr-1.5 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg> {t('dash.booksHealth')}
            </div>
            <div className="space-y-2">
              {/* Trial Balance Check */}
              <div className={`flex items-center justify-between p-2 rounded border shadow-sm ${
                stats.healthCheck.isBalanced
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-rose-50 border-rose-200'
              }`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${stats.healthCheck.isBalanced ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'}`} />
                  <span className="text-[11px] font-semibold text-gray-700">{t('dash.trialBalance')}</span>
                </div>
                <span className={`text-[11px] font-bold ${stats.healthCheck.isBalanced ? 'text-emerald-700' : 'text-rose-600'}`}>
                  {stats.healthCheck.isBalanced ? `✓ ${t('dash.balanced')}` : `✕ Diff: ${formatCurrency(stats.healthCheck.trialBalanceDiff)}`}
                </span>
              </div>

              {/* Liquid Cash */}
              <div className="flex items-center justify-between p-2 rounded border border-blue-200 bg-blue-50 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-[11px] font-semibold text-gray-700">{t('dash.liquidCash')}</span>
                </div>
                <span className="text-[13px] font-bold text-blue-700">{formatCurrency(stats.cashBankBalance)}</span>
              </div>

              {/* Overdue Receivables */}
              <div className={`flex items-center justify-between p-2 rounded border shadow-sm ${
                stats.healthCheck.overdueParties > 0
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${stats.healthCheck.overdueParties > 0 ? 'bg-amber-500' : 'bg-gray-400'}`} />
                  <span className="text-[11px] font-semibold text-gray-700">{t('dash.pendingDues')}</span>
                </div>
                {stats.healthCheck.overdueParties > 0 ? (
                  <span className="text-[11px] font-bold text-amber-700">
                    {stats.healthCheck.overdueParties} parties · {formatCurrency(stats.healthCheck.overdueAmount)}
                  </span>
                ) : (
                  <span className="text-[11px] font-bold text-gray-500">{t('dash.allClear')}</span>
                )}
              </div>

              {/* Activity Pulse */}
              <div className="flex items-center justify-between p-2 rounded border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#1e8585]" />
                  <span className="text-[11px] font-semibold text-gray-700">{t('dash.activity')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-gray-500">{t('dash.today')}: <strong className="text-gray-800">{stats.todayStats.voucherCount}</strong></span>
                  <span className="text-[10px] text-gray-500">7 days: <strong className="text-gray-800">{stats.healthCheck.last7DaysVouchers}</strong></span>
                  <span className="text-[10px] text-gray-500">Total: <strong className="text-gray-800">{stats.healthCheck.totalVouchers}</strong></span>
                </div>
              </div>

              {/* Net Profit/Loss */}
              <div className={`flex items-center justify-between p-2 rounded border shadow-sm ${
                stats.profitLoss >= 0
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-rose-50 border-rose-200'
              }`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${stats.profitLoss >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                  <span className="text-[11px] font-semibold text-gray-700">{stats.profitLoss >= 0 ? t('dash.netProfit') : t('dash.netLoss')}</span>
                </div>
                <span className={`text-[13px] font-bold ${stats.profitLoss >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                  {formatCurrency(stats.profitLoss)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  </div>
  );
};
